import React, { useContext, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildApiQuery } from "../../../helpers/timeRangeUtils";
import { useLicences } from "../../../context/LicenceContext";

// Third-party.
import HC_brokenAxis from "highcharts/modules/broken-axis";
import Boost from "highcharts/modules/boost";

import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';

// Context
import { InstanceContext } from '../../../context/InstanceContext';

// Types.
import GroupedStatement from '../../../types/instance/GroupedStatement';
import ChartPlotLine from '../../../types/ChartPlotLine';
import ChartSeries from '../../../types/ChartSeries';
import Time from '../../../types/instance/Time';
import ActivityExecution from '../../../types/instance/ActivityExecution';
import Execution from '../../../types/instance/Execution';
import Wait from '../../../types/instance/Wait';
import { ActivityBatchGroup } from "../../../types/instance/Activity";

// Constants.
import { CHART_DATA_OPTION, CHART_DATA_TYPE } from '../../Constants';
import { DATA_LOADED, DATA_LOADING, INSTANCE_TYPE_COCKROACHDB } from '../../Constants';
import { INSTANCE_TYPE_ORACLE, INSTANCE_TYPE_SQLSERVER } from '../../Constants';
import { CHART_COLOURS_STATEMENTS } from '../../Constants';

// Helpers
import Helper from '../../../helpers/Helper';
import { getConvertedTimeToUTC, highchartsCredits } from "../../../helpers/utils";
import { chartOptionsGranularity } from "../../../helpers/chartOptionsIncreasedGranularity";
import ConditionalRender from "../../../helpers/ConditionalRender";
import api from "../../../api/Base";
import ActivityApi from "../../../api/instance/ActivityApi";
import ClientsDataTable from "../../../types/instance/tables/ClientsDataTable";
import ProgramsDataTable from "../../../types/instance/tables/ProgramsDataTable";
import UsersDataTable from "../../../types/instance/tables/UsersDataTable";
import SessionsDataTable from "../../../types/instance/tables/SessionsDataTable";
import DatabasesDataTable from "../../../types/instance/tables/DatabasesDataTable";

HC_brokenAxis(Highcharts);
Boost(Highcharts);

function GroupedTimeChart(props: {
    dataOption: CHART_DATA_OPTION,
    instanceId: number,
    batchId?: string,
    statementId?: string,
    statements: GroupedStatement[],
    plotLines: ChartPlotLine[],
    clients: ClientsDataTable[],
    programs: ProgramsDataTable[],
    users: UsersDataTable[],
    sessions: SessionsDataTable[],
    databases: DatabasesDataTable[],
    waits: Wait[],
    batches: ActivityBatchGroup[],
    filters: any
}) {
    const {instances} = useContext(InstanceContext);
    const {licences} = useLicences();
    const timeRangeContext = useContext(TimeRangeContext)
    const isAutorefresh = timeRangeContext.timeRange.autorefresh
    const navigate = useNavigate();
    const chartRef = useRef(null);

    const [loading, setLoading] = useState<number>(DATA_LOADING);

    const [timeSeries, setTimeSeries] = useState<ChartSeries[]>([]);
    const [waitSeries, setWaitSeries] = useState<number[][]>([]);
    const [statementSeries, setStatementSeries] = useState<ChartSeries[]>([]);
    const [batchSeries, setBatchSeries] = useState<ChartSeries[]>([]);
    const [executionSeries, setExecutionSeries] = useState<ChartSeries[]>([]);
    const [clientsSeries, setClientsSeries] = useState<number[][]>([]);
    const [programsSeries, setProgramsSeries] = useState<number[][]>([]);
    const [usersSeries, setUsersSeries] = useState<number[][]>([]);
    const [sessionsSeries, setSessionsSeries] = useState<number[][]>([]);
    const [databasesSeries, setDatabasesSeries] = useState<number[][]>([]);

    let batchId: string = '',
        statementId: string = '';

    if (props.batchId) {
        batchId = props.batchId;
    }

    if (props.statementId) {
        statementId = props.statementId;
    }

    const timePeak = useMemo(() => {
        // @ts-ignore
        return Helper.getPeakValue(timeSeries.map(i => i[1]))
    }, [timeSeries])

    const executionPeak = useMemo(() => {
        // @ts-ignore
        return Helper.getPeakValue(executionSeries.map(i => i[1]))
    }, [executionSeries])

    void useMemo(async() => {
        if (props.dataOption === CHART_DATA_OPTION.STATEMENTS) {
            setLoading(DATA_LOADING);
            const statementPromises = []
            let groupStatementSeries: any[] = [];

            // Get the top individual statements for the period.
            for (let index = 0; index < props.statements.slice(0, 10).length; index++) {
                let statement: GroupedStatement = props.statements[index];
                if (statement.grouphash !== null) {
                    statementPromises.push(ActivityApi.getTime(buildApiQuery(timeRangeContext.timeRange), props.instanceId, `&grouphash=${statement.grouphash}${props.filters}&component=GroupedTimeChart`))
                }
            }

            const responsesStatements = await Promise.all(statementPromises)
            responsesStatements.forEach((response: { data: Time[]; }, index: number) => {
                let statement: GroupedStatement = props.statements[index];
                let statementData: number[][] = [];

                statementData = statementData.concat(response.data.map(item => {
                    return [getConvertedTimeToUTC(item), item.waittime]
                }));

                // Add the chart series.
                groupStatementSeries.push({
                    name: ((statement.grouphash === null) ? '-' : statement.grouphash),
                    color: statement.color,
                    cursor: 'pointer',
                    type: 'column',
                    data: statementData,
                    tooltip: {
                        valueSuffix: 'ms',
                    },
                    url: `/instances/${props.instanceId}/activity/groupedstatement/${statement.grouphash}?${buildApiQuery(timeRangeContext.timeRange)}`,
                    zIndex: groupStatementSeries.length
                });
            })

            setStatementSeries(groupStatementSeries);
            setLoading(DATA_LOADED);
        }
    }, [timeRangeContext, props.instanceId, props.statements, props.dataOption])

    void useMemo(async() => {
        if (props.dataOption === CHART_DATA_OPTION.BATCHES) {
            setLoading(DATA_LOADING);
            const batchPromises = []
            let groupBatchSeries: any[] = [];

            // Get the top individual batches for the period.
            for (let index = 0; index < props.batches.slice(0, 10).length; index++) {
                let batch: ActivityBatchGroup = props.batches[index];
                if (batch.batchgrouphash !== null) {
                    batchPromises.push(ActivityApi.getTime(buildApiQuery(timeRangeContext.timeRange), props.instanceId, `&batchgrouphash=${batch.batchgrouphash}${props.filters}&component=GroupedTimeChart`))
                }
            }

            const batchResponses = await Promise.all(batchPromises)
            batchResponses.forEach((response: { data: Time[]; }, index: number) => {
                let batch: ActivityBatchGroup = props.batches[index];
                let groupedBatchData: number[][] = [];

                groupedBatchData = groupedBatchData.concat(response.data.map(item => {
                    return [getConvertedTimeToUTC(item), item.waittime]
                }));

                // Add the chart series.
                groupBatchSeries.push({
                    name: ((batch.batchgrouphash === null) ? '-' : batch.batchgrouphash),
                    color: CHART_COLOURS_STATEMENTS?.[index] ?? '',
                    data: groupedBatchData,
                    type: 'column',
                    tooltip: {
                        valueSuffix: 'ms',
                    },
                    url: `/instances/${props.instanceId}/activity/groupedbatch/${batch.batchgrouphash}?${buildApiQuery(timeRangeContext.timeRange)}`,
                    zIndex: groupBatchSeries.length
                });
            })
            setBatchSeries(groupBatchSeries);
            setLoading(DATA_LOADED);
        }
    }, [timeRangeContext, props.instanceId, props.batches, props.dataOption])

    void useMemo(async() => {
        if (props.dataOption === CHART_DATA_OPTION.WAITS) {
            setLoading(DATA_LOADING);

            const waitsPromises = []
            let waitSeries: any[] = []

            let batchApi: string = '',
                statementApi: string = '';

            if (batchId.length > 0) {
                batchApi = `&batchgrouphash=${batchId}`;
            }

            if (statementId.length > 0) {
                statementApi = `&grouphash=${statementId}`;
            }

            // Get the top individual waits for the period.
            for (let index = 0; index < props.waits.slice(0, 10).length; index++) {
                let wait: Wait = props.waits[index];
                waitsPromises.push(ActivityApi.getTime(buildApiQuery(timeRangeContext.timeRange), props.instanceId, `&waitevent=${encodeURIComponent(wait.waitevent)}${batchApi}${statementApi}${props.filters}&component=GroupedTimeChart`))
            }

            const responses = await Promise.all(waitsPromises)
            responses.forEach((response: { data: Time[]; }, index: number) => {
                let wait: Wait = props.waits[index];
                let waitData: number[][] = [];

                waitData = waitData.concat(response.data.map(item => {
                    return [getConvertedTimeToUTC(item), item.waittime]
                }));

                // Add the chart series.
                waitSeries.push({
                    name: wait.waitevent,
                    color: wait.color,
                    type: 'column',
                    data: waitData,
                    tooltip: {
                        valueSuffix: 'ms',
                    },
                    zIndex: 1
                });
            })

            setWaitSeries(waitSeries);
            setLoading(DATA_LOADED);
        }
    }, [batchId, statementId, timeRangeContext, props.instanceId, props.waits, props.dataOption])

    void useMemo(async() => {
        if (props.dataOption === CHART_DATA_OPTION.ACTIVITY) {
            setLoading(DATA_LOADING);

            let batchApi: string = '',
                statementApi: string = '';

            if (batchId.length > 0) {
                batchApi = `&batchgrouphash=${batchId}`;
            }

            if (statementId.length > 0) {
                statementApi = `&grouphash=${statementId}`;
            }

            // Get time for the period.
            ActivityApi.getTime(buildApiQuery(timeRangeContext.timeRange), props.instanceId, `&limit=${process.env.REACT_APP_API_LIMIT}${batchApi}${statementApi}${props.filters}&component=GroupedTimeChart`)
                .then((response: { data: Time[]; }) => {
                    // @ts-ignore
                    setTimeSeries(response.data.map(item => {
                        return [getConvertedTimeToUTC(item), item.waittime]
                    }));
                    setLoading(DATA_LOADED);
                })
                .catch((error: any) => {
                    console.error('Failed to retrieve activity time.', error);
                })
        }
    }, [batchId, timeRangeContext, props.instanceId, statementId, props.dataOption])

    void useMemo(() => {
        let showExecutions: boolean = !(batchId.length && statementId.length)
        let batchApi: string = '',
            statementApi: string = '';

        if (batchId.length > 0) {
            batchApi = `&batchgrouphash=${batchId}`;
        }

        if (statementId.length > 0) {
            statementApi = `&grouphash=${statementId}`;
        }

        // Get the current instance.
        const matchedInstances = instances.filter(instance => Number(instance.id) === Number(props.instanceId));

        if (matchedInstances.length > 0) {
            switch (matchedInstances[0].type) {
                case INSTANCE_TYPE_ORACLE:
                case INSTANCE_TYPE_SQLSERVER:
                case INSTANCE_TYPE_COCKROACHDB:
                    showExecutions = true;
                    break;

                default:
                    break;
            }
        }

        if (showExecutions) {
            if (props.filters) {
                ActivityApi.getTime(buildApiQuery(timeRangeContext.timeRange), props.instanceId, `&limit=${process.env.REACT_APP_API_LIMIT}&statistic=executions${batchApi}${statementApi}${props.filters}&component=GroupedTimeChart`)
                    .then((response: { data: ActivityExecution[]; }) => {
                        // @ts-ignore
                        setExecutionSeries(response.data.map(item => {
                            return [getConvertedTimeToUTC(item), item.executions]
                        }));
                    })
                    .catch((error: any) => {
                        console.error('Failed to retrieve executions statistic.', error);
                    })
            } else {
                api.get(`statistic/time?${buildApiQuery(timeRangeContext.timeRange)}&limit=${process.env.REACT_APP_API_LIMIT}&statistic=executions&id=${props.instanceId}${batchApi}${statementApi}${props.filters}&component=GroupedTimeChart`)
                    .then((response: { data: Execution[]; }) => {
                        // @ts-ignore
                        setExecutionSeries(response.data.map(item => {
                            return [getConvertedTimeToUTC(item), item.sum]
                        }));
                    })
                    .catch((error: any) => {
                        console.error('Failed to retrieve executions statistic.', error);
                    })
            }
        }
		// Instances are are dependent on period in Context.tsx,
		// which results in a double fetch.
    }, [batchId, statementId, timeRangeContext, props.instanceId/*, instances*/, props.dataOption])

    const useFetchChartData = ({
                                   dataOption,
                                   dataKey,
                                   apiParam,
                                   idKey,
                                   setSeries,
                                   props,
                                   timeRangeContext
                               }: any) => {
        void useMemo(async () => {
            if (props.dataOption !== dataOption) return;
            setLoading(DATA_LOADING);

            const promises = props[dataKey].slice(0, 10).map((item: any) => {
                if (item[idKey] !== null) {
                    return ActivityApi.getTime(buildApiQuery(timeRangeContext.timeRange), props.instanceId, `&${apiParam}=${encodeURIComponent(item[idKey])}${props.filters}`);
                }
                return null;
            }).filter(Boolean);

            const responses = await Promise.all(promises);
            const seriesData = responses.map((response, index) => {
                const item = props[dataKey][index];
                const data = response.data.map((entry: any) => [getConvertedTimeToUTC(entry), entry.waittime]);
                return {
                    name: item[idKey] || "-",
                    color: CHART_COLOURS_STATEMENTS[index],
                    type: "column" as const,
                    data,
                    tooltip: { valueSuffix: "ms" },
                    url: `/instances/${props.instanceId}/activity/${dataKey}/${encodeURIComponent(item[idKey])}?${buildApiQuery(timeRangeContext.timeRange)}`,
                    zIndex: index
                };
            });

            setSeries(seriesData);
            setLoading(DATA_LOADED);
        }, [timeRangeContext, props.instanceId, props[dataKey], props.dataOption]);
    };

    useFetchChartData({ dataOption: CHART_DATA_OPTION.CLIENTS, dataKey: "clients", apiParam: "clients", idKey: "client", setSeries: setClientsSeries, props, timeRangeContext });
    useFetchChartData({ dataOption: CHART_DATA_OPTION.PROGRAMS, dataKey: "programs", apiParam: "programs", idKey: "program", setSeries: setProgramsSeries, props, timeRangeContext });
    useFetchChartData({ dataOption: CHART_DATA_OPTION.USERS, dataKey: "users", apiParam: "users", idKey: "username", setSeries: setUsersSeries, props, timeRangeContext });
    useFetchChartData({ dataOption: CHART_DATA_OPTION.SESSIONS, dataKey: "sessions", apiParam: "sessions", idKey: "session", setSeries: setSessionsSeries, props, timeRangeContext });
    useFetchChartData({ dataOption: CHART_DATA_OPTION.DATABASES, dataKey: "databases", apiParam: "databases", idKey: "database", setSeries: setDatabasesSeries, props, timeRangeContext });

    const timeChart = useMemo(() => {
        if (props.dataOption === CHART_DATA_OPTION.ACTIVITY) {
            const timeChartOptions = chartOptionsGranularity([
                {
                    type: 'column', data: timeSeries, name: 'Time Spent', zIndex: 1, tooltip: {valueSuffix: 'ms',}
                },
                ...(!props.filters
                        ? [

                            {
                                type: 'spline',
                                minHeight: 3,
                                data: executionSeries,
                                name: 'Executions',
                                zIndex: 1,
                                yAxis: 1
                            },
                        ]
                        : []
                )], timePeak, {
                credits: {enabled: highchartsCredits(licences)},
                legend: true,
                timeRangeContext: timeRangeContext,
                plotLines: props.plotLines,
                tooltip: {
                    formatter: function () {
                        return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.TIME);
                    },
                },
            })
            return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts}
                                    options={timeChartOptions}/>;
        }

        return null
    }, [executionSeries, timeSeries, timePeak, timeRangeContext, props.plotLines, props.dataOption, licences]);

    const generateChart = (dataOption: number, chartSeries: number[][]) => {
        if (props.dataOption === dataOption) {
            const chartOptions = chartOptionsGranularity([
                ...chartSeries,
                ...(!props.filters
                                ? [
                                    {
                                        type: 'spline',
                                        minHeight: 3,
                                        data: executionSeries,
                                        name: 'Executions',
                                        zIndex: 999,
                                        yAxis: 1,
                                    },
                                ]
                                : []
                )
            ], timePeak, {
                credits: { enabled: highchartsCredits(licences) },
                legend: true,
                timeRangeContext: timeRangeContext,
                plotLines: props.plotLines,
                tooltip: {
                    formatter: function () {
                        return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.TIME);
                    },
                },
            });

            return <HighchartsReact constructorType={"chart"} highcharts={Highcharts} options={chartOptions}/>;
        }

        return null;
    };

    const waitsChart = useMemo(() => generateChart(CHART_DATA_OPTION.WAITS, waitSeries), [
        executionSeries, waitSeries, timePeak, props.plotLines, props.dataOption, licences, props.clients
    ]);

    const clientsChart = useMemo(() => generateChart(CHART_DATA_OPTION.CLIENTS, clientsSeries), [
        executionSeries, clientsSeries, timePeak, props.plotLines, props.dataOption, licences, props.clients
    ]);

    const programsChart = useMemo(() => generateChart(CHART_DATA_OPTION.PROGRAMS, programsSeries), [
        executionSeries, programsSeries, timePeak, props.plotLines, props.dataOption, licences, props.programs
    ]);

    const usersChart = useMemo(() => generateChart(CHART_DATA_OPTION.USERS, usersSeries), [
        executionSeries, usersSeries, timePeak, props.plotLines, props.dataOption, licences, props.users
    ]);

    const sessionsChart = useMemo(() => generateChart(CHART_DATA_OPTION.SESSIONS, sessionsSeries), [
        executionSeries, sessionsSeries, timePeak, props.plotLines, props.dataOption, licences, props.sessions
    ]);

    const databasesChart = useMemo(() => generateChart(CHART_DATA_OPTION.DATABASES, databasesSeries), [
        executionSeries, databasesSeries, timePeak, props.plotLines, props.dataOption, licences, props.sessions
    ]);

    const statementsChart = useMemo(() => {
        if (props.dataOption === CHART_DATA_OPTION.STATEMENTS) {
            const statementsChartOptions = chartOptionsGranularity([...statementSeries,
                ...(!props.filters
                        ? [
                            {
                                type: 'spline',
                                minHeight: 3,
                                data: executionSeries,
                                name: 'Executions',
                                zIndex: 999,
                                yAxis: 1,
                            },
                        ]
                        : []
                )], timePeak, {
                credits: {enabled: highchartsCredits(licences)},
                legend: true,
                timeRangeContext: timeRangeContext,
                plotLines: props.plotLines,
                tooltip: {
                    formatter: function () {
                        return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.TIME);
                    },
                },
            })
            // @ts-ignore
            statementsChartOptions.plotOptions.series.events = {
                click: function () {
                    const column: any = this;
                    if (column.name !== 'Executions') {
                        navigate(`/instances/${props.instanceId}/activity/groupedstatement/${column.name}?${buildApiQuery(timeRangeContext.timeRange)}`);
                    }
                }
            }

            return <HighchartsReact constructorType={"chart"} highcharts={Highcharts}
                                    options={statementsChartOptions}/>;
        }

        return null

    }, [props.plotLines, props.instanceId, props.dataOption, timeRangeContext, executionSeries, statementSeries, timePeak, licences]);

    const batchesChart = useMemo(() => {
        if (props.dataOption === CHART_DATA_OPTION.BATCHES) {
            const batchesChartOptions = chartOptionsGranularity([...batchSeries,
                ...(!props.filters
                        ? [
                            {
                                type: 'spline',
                                minHeight: 3,
                                data: executionSeries,
                                name: 'Executions',
                                zIndex: 999,
                                yAxis: 1,
                            },
                        ]
                        : []
                )], timePeak, {
                credits: {enabled: highchartsCredits(licences)},
                legend: true,
                timeRangeContext: timeRangeContext,
                plotLines: props.plotLines,
                tooltip: {
                    formatter: function () {
                        return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.TIME);
                    },
                },
            })
            // @ts-ignore
            batchesChartOptions.plotOptions.series.events = {
                click: function () {
                    const column: any = this;
                    if (column.name !== 'Executions') {
                        navigate(`/instances/${props.instanceId}/activity/groupedbatch/${column.name}${buildApiQuery(timeRangeContext.timeRange)}`);
                    }
                }
            }

            return <HighchartsReact constructorType={"chart"} highcharts={Highcharts} options={batchesChartOptions}/>;
        }

        return null
    }, [executionSeries, batchSeries, timeRangeContext, timePeak, props.dataOption, props.plotLines, licences, props.instanceId]);

    /*
           Note: From reading various posts and articles, passing new options doesn't work as the chart is already rendered, and third-party React components/wrappers
           for Highcharts actually destroy and recreate the Highchart object as they can't get it to work either.  Ultimately, we want to use "chart.update".
       */

    const chartMap = {
        [CHART_DATA_OPTION.ACTIVITY]: timeChart,
        [CHART_DATA_OPTION.BATCHES]: batchesChart,
        [CHART_DATA_OPTION.STATEMENTS]: statementsChart,
        [CHART_DATA_OPTION.WAITS]: waitsChart,
        [CHART_DATA_OPTION.CLIENTS]: clientsChart,
        [CHART_DATA_OPTION.PROGRAMS]: programsChart,
        [CHART_DATA_OPTION.USERS]: usersChart,
        [CHART_DATA_OPTION.SESSIONS]: sessionsChart,
        [CHART_DATA_OPTION.DATABASES]: databasesChart,
    };

    // @ts-ignore
    const selectedChart = chartMap[props.dataOption];

    return (
        <React.Fragment>
            <ConditionalRender if={loading === DATA_LOADING && !isAutorefresh}>
                <div className="w-100 text-center time-chart-center text-muted mt-3">
                    <div className="loader spinner chartSpinner">
                    </div>
                    <p>
                        Loading data...
                    </p>
                </div>
            </ConditionalRender>

            <ConditionalRender if={isAutorefresh || loading === DATA_LOADED}>
                    <span className="peak">
                        {(timePeak === Infinity || timePeak === -Infinity) ? (
                                <React.Fragment>Peak Time: -</React.Fragment>
                        ) : (
                                <React.Fragment>Peak Time: {Helper.getTimeInEnglish(timePeak)}</React.Fragment>
                        )}
                        {batchId.length === 0 && (
                                <React.Fragment>,
                                    Executions: {executionPeak.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</React.Fragment>
                        )}
                    </span>
                <br/>
                <div className={selectedChart ? 'mt-35' : 'd-none'}>
                    <div ref={chartRef}>
                        {selectedChart}
                    </div>
                </div>
            </ConditionalRender>
        </React.Fragment>
    );
}

GroupedTimeChart.defaultProps = {
    statements: []
};

export default GroupedTimeChart;
